import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Avatar,
  Grid,
  Card,
  CardContent,
  Divider,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import SendIcon from '@mui/icons-material/Send';
import VerifiedIcon from '@mui/icons-material/Verified';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import InventoryIcon from '@mui/icons-material/Inventory';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import GroupIcon from '@mui/icons-material/Group';
import FactoryIcon from '@mui/icons-material/Factory';
import DeleteIcon from '@mui/icons-material/Delete';
import CircularProgress from '@mui/material/CircularProgress';
import { useNavigate, useParams } from 'react-router-dom';
import {
  productionOrdersApi,
  ProductionOrder,
  ProductionOrderStatus,
  MaterialIssue,
  IssueMaterialItem,
  ReceiveMaterialItem,
} from '../api/productionOrders';
import { designBomApi, DesignBOMItem } from '../api/designBom';

const STATUS_STEPS = ['DRAFT', 'CUTTING', 'STITCHING', 'FINISHING', 'COMPLETED'];
const STATUS_LABELS: Record<string, string> = {
  DRAFT: 'Draft', READY: 'Ready', CUTTING: 'Cutting', STITCHING: 'Stitching',
  FINISHING: 'Finishing', COMPLETED: 'Completed', ON_HOLD: 'On Hold', CANCELLED: 'Cancelled',
};
const STATUS_COLORS: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  DRAFT: 'default', READY: 'info', CUTTING: 'primary', STITCHING: 'warning',
  FINISHING: 'secondary', COMPLETED: 'success', ON_HOLD: 'default', CANCELLED: 'error',
};

const SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL'];
const SIZE_FIELDS: Record<string, string> = {
  XS: 'xs_pieces', S: 's_pieces', M: 'm_pieces', L: 'l_pieces',
  XL: 'xl_pieces', XXL: 'xxl_pieces', '3XL': 'three_xl_pieces',
};

// ═══════════════════════════════════════════════════════════════════
// Interfaces for dialog rows
// ═══════════════════════════════════════════════════════════════════

interface IssueMaterialRow {
  bom_item_id: number;
  material_name: string;
  material_type: string;
  material_code: string | null;
  material_image: string | null;
  garment_part: string;
  uom: string;
  planned_qty: number;
  reserved_qty: number;
  already_issued: number;
  issued_qty: number;
  notes: string;
}

interface ReceiveMaterialRow {
  issue_id: number;
  material_name: string;
  material_type: string;
  garment_part: string;
  uom: string;
  issued_qty: number;
  received_qty: number;
  discrepancy_notes: string;
}

export default function ProductionOrderDetailPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [order, setOrder] = useState<ProductionOrder | null>(null);
  const [bomItems, setBomItems] = useState<DesignBOMItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [issuing, setIssuing] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState<ProductionOrderStatus>('DRAFT');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({ open: false, message: '', severity: 'info' });

  // Material Issue state
  const [issueDialogOpen, setIssueDialogOpen] = useState(false);
  const [issueRows, setIssueRows] = useState<IssueMaterialRow[]>([]);
  const [issuingMaterials, setIssuingMaterials] = useState(false);

  // Material Receive state
  const [receiveDialogOpen, setReceiveDialogOpen] = useState(false);
  const [receiveRows, setReceiveRows] = useState<ReceiveMaterialRow[]>([]);
  const [receivingMaterials, setReceivingMaterials] = useState(false);


  // ═══════════════════════════════════════════════════════════════════
  // Data fetching
  // ═══════════════════════════════════════════════════════════════════

  const fetchOrder = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const data = await productionOrdersApi.getById(Number(id));
      setOrder(data);
    } catch (error: any) {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Failed to fetch order', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchBom = useCallback(async () => {
    if (!order?.design_no) return;
    try {
      const data = await designBomApi.getByDesign(order.design_no);
      setBomItems(data.bomItems);
    } catch (error) {
      console.error('Failed to fetch BOM:', error);
    }
  }, [order?.design_no]);

  useEffect(() => { fetchOrder(); }, [fetchOrder]);
  useEffect(() => { fetchBom(); }, [fetchBom]);

  // ═══════════════════════════════════════════════════════════════════
  // Core action handlers
  // ═══════════════════════════════════════════════════════════════════

  const handleIssue = async () => {
    if (!id) return;
    setIssuing(true);
    try {
      const result = await productionOrdersApi.issue(Number(id));
      setOrder(result.order);
      const reserved = result.reservations.filter((r) => r.status === 'RESERVED').length;
      const partial = result.reservations.filter((r) => r.status === 'PARTIAL').length;
      const notFound = result.reservations.filter((r) => r.status === 'NOT_FOUND').length;
      let msg = `Issued to production. ${reserved} material(s) fully reserved.`;
      if (partial > 0) msg += ` ${partial} partial.`;
      if (notFound > 0) msg += ` ${notFound} not found in allocations.`;
      setSnackbar({ open: true, message: msg, severity: reserved + partial > 0 ? 'success' : 'info' });
    } catch (error: any) {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Failed to issue order', severity: 'error' });
    } finally {
      setIssuing(false);
    }
  };

  const handleRefreshStatus = async () => {
    if (!id) return;
    try {
      const updated = await productionOrdersApi.refreshStatus(Number(id));
      setOrder(updated);
      setSnackbar({ open: true, message: 'Status refreshed', severity: 'success' });
    } catch (error: any) {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Failed to refresh', severity: 'error' });
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    if (!window.confirm('Are you sure you want to delete this production order? Material reservations will be released.')) return;
    try {
      await productionOrdersApi.delete(Number(id));
      setSnackbar({ open: true, message: 'Order deleted', severity: 'success' });
      navigate('/production-orders');
    } catch (error: any) {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Failed to delete order', severity: 'error' });
    }
  };

  const handleStatusChange = async () => {
    if (!id) return;
    try {
      await productionOrdersApi.update(Number(id), { status: newStatus });
      setStatusDialogOpen(false);
      setSnackbar({ open: true, message: `Status changed to ${STATUS_LABELS[newStatus]}`, severity: 'success' });
      fetchOrder();
    } catch (error: any) {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Failed to update status', severity: 'error' });
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // Material Issue (Merchandiser → Production)
  // ═══════════════════════════════════════════════════════════════════

  const openIssueDialog = () => {
    if (!order?.bom_items) return;
    const rows: IssueMaterialRow[] = order.bom_items
      .filter((item: any) => item.planned_qty > 0)
      .map((item: any) => {
        const alreadyIssued = (order.material_issues || [])
          .filter((mi) => mi.production_order_bom_id === item.id)
          .reduce((sum, mi) => sum + mi.issued_qty, 0);
        const remaining = Math.round(Math.max(0, item.planned_qty - alreadyIssued));
        return {
          bom_item_id: item.id,
          material_name: item.material_name,
          material_type: item.material_type,
          material_code: item.material?.material_code || null,
          material_image: item.material?.image_url || null,
          garment_part: item.garment_part,
          uom: item.uom,
          planned_qty: item.planned_qty,
          reserved_qty: item.issued_qty || 0,
          already_issued: Math.round(alreadyIssued),
          issued_qty: remaining,
          notes: '',
        };
      });
    setIssueRows(rows);
    setIssueDialogOpen(true);
  };

  const handleIssueMaterials = async () => {
    if (!id || issueRows.length === 0) return;
    setIssuingMaterials(true);
    try {
      const items: IssueMaterialItem[] = issueRows
        .filter((r) => r.issued_qty > 0)
        .map((r) => ({ bom_item_id: r.bom_item_id, issued_qty: Math.round(r.issued_qty), notes: r.notes || undefined }));
      if (items.length === 0) {
        setSnackbar({ open: true, message: 'No materials to issue (all quantities are 0)', severity: 'info' });
        setIssuingMaterials(false);
        return;
      }
      const result = await productionOrdersApi.issueMaterials(Number(id), items);
      setSnackbar({ open: true, message: result.message, severity: 'success' });
      setIssueDialogOpen(false);
      fetchOrder();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to issue materials', severity: 'error' });
    } finally {
      setIssuingMaterials(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // Material Receive (Production Person Confirms)
  // ═══════════════════════════════════════════════════════════════════

  const openReceiveDialog = () => {
    const pending = (order?.material_issues || []).filter((mi) => mi.status === 'ISSUED');
    if (pending.length === 0) return;
    const rows: ReceiveMaterialRow[] = pending.map((mi) => ({
      issue_id: mi.id, material_name: mi.material_name, material_type: mi.material_type,
      garment_part: mi.garment_part, uom: mi.uom, issued_qty: mi.issued_qty,
      received_qty: mi.issued_qty, discrepancy_notes: '',
    }));
    setReceiveRows(rows);
    setReceiveDialogOpen(true);
  };

  const handleReceiveMaterials = async () => {
    if (!id || receiveRows.length === 0) return;
    setReceivingMaterials(true);
    try {
      const items: ReceiveMaterialItem[] = receiveRows.map((r) => ({
        issue_id: r.issue_id, received_qty: r.received_qty,
        discrepancy_notes: r.received_qty !== r.issued_qty ? r.discrepancy_notes || undefined : undefined,
      }));
      const result = await productionOrdersApi.receiveMaterials(Number(id), items);
      setSnackbar({ open: true, message: result.message, severity: result.has_discrepancies ? 'info' : 'success' });
      setReceiveDialogOpen(false);
      fetchOrder();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to receive materials', severity: 'error' });
    } finally {
      setReceivingMaterials(false);
    }
  };


  // ═══════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════

  if (loading) return <Box sx={{ p: 3 }}><LinearProgress /></Box>;
  if (!order) return <Box sx={{ p: 3 }}><Alert severity="error">Order not found</Alert></Box>;

  const activeStep = STATUS_STEPS.indexOf(order.status);
  const progress = order.progress || { total_planned: order.total_planned, total_cut: order.total_cut, total_stitched: order.total_stitched, total_finished: order.total_finished };
  const plannedQty = order.planned_qty as Record<string, Record<string, number>> | null;

  const materialIssues = order.material_issues || [];
  const pendingIssues = materialIssues.filter((mi) => mi.status === 'ISSUED');
  const completedIssues = materialIssues.filter((mi) => mi.status === 'RECEIVED' || mi.status === 'DISCREPANCY');
  const hasBomItems = (order.bom_items || []).length > 0;
  const allMaterialsIssued = hasBomItems && materialIssues.length > 0;
  const allMaterialsReceived = allMaterialsIssued && pendingIssues.length === 0 && completedIssues.length > 0;

  const isNotDraftCancelled = order.status !== 'DRAFT' && order.status !== 'CANCELLED';
  const statusIdx = STATUS_STEPS.indexOf(order.status);
  const showCutting = isNotDraftCancelled && order.status !== 'ON_HOLD';
  const showStitching = showCutting && statusIdx >= STATUS_STEPS.indexOf('CUTTING');
  const showFinishing = showCutting && statusIdx >= STATUS_STEPS.indexOf('STITCHING');

  const cuttingRecords = order.cutting_records || [];
  const workerAssignments = order.worker_assignments || [];
  const productionBatches = order.production_batches || [];

  return (
    <Box sx={{ p: isMobile ? 1 : 3 }}>
      {/* ═══ Header ═══ */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/production-orders')}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={700}>{order.order_number}</Typography>
          <Typography variant="body2" color="text.secondary">Design: {order.design_no} {order.design?.name ? `- ${order.design.name}` : ''}</Typography>
        </Box>
        {order.design?.image_url && (
          <Avatar src={order.design.image_url} variant="rounded" sx={{ width: 56, height: 56 }} />
        )}
      </Box>

      {/* ═══ Status Stepper ═══ */}
      {order.status !== 'CANCELLED' && order.status !== 'ON_HOLD' && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Stepper activeStep={activeStep} alternativeLabel={!isMobile} orientation={isMobile ? 'vertical' : 'horizontal'}>
            {STATUS_STEPS.map((step) => (
              <Step key={step} completed={STATUS_STEPS.indexOf(step) < activeStep}>
                <StepLabel>{STATUS_LABELS[step]}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>
      )}

      {/* ═══ Summary Cards ═══ */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Planned', value: progress.total_planned, color: undefined },
          { label: 'Cut', value: progress.total_cut, color: 'primary.main' },
          { label: 'Stitched', value: progress.total_stitched, color: 'warning.main' },
          { label: 'Finished', value: progress.total_finished, color: 'success.main' },
        ].map((c) => (
          <Grid item xs={3} key={c.label}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 1.5, px: 1 }}>
                <Typography variant="caption" color="text.secondary">{c.label}</Typography>
                <Typography variant="h5" fontWeight={700} color={c.color}>{c.value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* ═══ Actions ═══ */}
      <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        <Chip label={STATUS_LABELS[order.status]} color={STATUS_COLORS[order.status]} />
        <Chip label={order.priority} variant="outlined" size="small" />
        {order.target_date && (
          <Chip label={`Target: ${new Date(order.target_date).toLocaleDateString('en-IN')}`} variant="outlined" size="small" />
        )}
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Refresh Status">
          <IconButton size="small" onClick={handleRefreshStatus}><RefreshIcon /></IconButton>
        </Tooltip>
        {!order.cutting_start_date && !order.total_cut && order.status !== 'COMPLETED' && (
          <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={handleDelete}>
            Delete
          </Button>
        )}
        {order.status === 'DRAFT' && (
          <Button size="small" variant="contained" startIcon={issuing ? <CircularProgress size={14} color="inherit" /> : <SendIcon />} onClick={handleIssue} disabled={issuing}>
            Issue to Production
          </Button>
        )}
        {order.status !== 'COMPLETED' && order.status !== 'CANCELLED' && (
          <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => { setNewStatus(order.status); setStatusDialogOpen(true); }}>
            Status
          </Button>
        )}
      </Box>

      {/* ═══ Order Details ═══ */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>Order Details</Typography>
        <Grid container spacing={2}>
          {[
            { label: 'Created By', value: order.created_by || '-' },
            { label: 'Created At', value: new Date(order.created_at).toLocaleDateString('en-IN') },
            { label: 'Cutting Started', value: order.cutting_start_date ? new Date(order.cutting_start_date).toLocaleDateString('en-IN') : '-' },
            { label: 'Stitching Started', value: order.stitching_start_date ? new Date(order.stitching_start_date).toLocaleDateString('en-IN') : '-' },
          ].map((d) => (
            <Grid item xs={6} sm={3} key={d.label}>
              <Typography variant="caption" color="text.secondary">{d.label}</Typography>
              <Typography variant="body2">{d.value}</Typography>
            </Grid>
          ))}
        </Grid>
        {order.notes && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">Notes</Typography>
            <Typography variant="body2">{order.notes}</Typography>
          </Box>
        )}
      </Paper>

      {/* ═══ Planned Quantities ═══ */}
      {plannedQty && Object.keys(plannedQty).length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>Planned Quantities</Typography>
          {isMobile ? (
            Object.entries(plannedQty).map(([part, sizes]) => (
              <Box key={part} sx={{ mb: 1 }}>
                <Typography variant="body2" fontWeight={600}>{part}</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {SIZES.filter((s) => (sizes as any)[s]).map((s) => (
                    <Chip key={s} label={`${s}: ${(sizes as any)[s]}`} size="small" variant="outlined" />
                  ))}
                  <Chip label={`Total: ${Object.values(sizes).reduce((a, b) => a + (Number(b) || 0), 0)}`} size="small" color="primary" />
                </Box>
              </Box>
            ))
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Part</TableCell>
                    {SIZES.map((s) => <TableCell key={s} align="center">{s}</TableCell>)}
                    <TableCell align="center">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(plannedQty).map(([part, sizes]) => (
                    <TableRow key={part}>
                      <TableCell>{part}</TableCell>
                      {SIZES.map((s) => <TableCell key={s} align="center">{(sizes as any)[s] || '-'}</TableCell>)}
                      <TableCell align="center" sx={{ fontWeight: 600 }}>{Object.values(sizes).reduce((a, b) => a + (Number(b) || 0), 0)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* ═══ BOM (Material Requirements) ═══ */}
      {order.bom_items && order.bom_items.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>Material Requirements (BOM)</Typography>
          {isMobile ? (
            order.bom_items.map((item: any) => {
              const dm = item.design_material;
              return (
                <Card key={item.id} variant="outlined" sx={{ mb: 1, p: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" fontWeight={600}>{item.material_name}</Typography>
                    <Chip label={item.is_fulfilled ? 'Reserved' : item.issued_qty > 0 ? 'Partial' : dm ? 'Linked' : 'No Alloc'} size="small" color={item.is_fulfilled ? 'success' : item.issued_qty > 0 ? 'warning' : dm ? 'info' : 'default'} />
                  </Box>
                  <Typography variant="caption" color="text.secondary">{item.material_type} | {item.garment_part}</Typography>
                  <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                    <Typography variant="body2">Required: {Math.round(item.planned_qty)} {item.uom}</Typography>
                    {item.issued_qty > 0 && <Typography variant="body2" color="success.main">Reserved: {Math.round(item.issued_qty)}</Typography>}
                  </Box>
                </Card>
              );
            })
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Material</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Part</TableCell>
                    <TableCell align="right">Required</TableCell>
                    <TableCell align="right">Reserved</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {order.bom_items.map((item: any) => {
                    const dm = item.design_material;
                    return (
                      <TableRow key={item.id}>
                        <TableCell>{item.material_name}</TableCell>
                        <TableCell>{item.material_type}</TableCell>
                        <TableCell>{item.garment_part}</TableCell>
                        <TableCell align="right">{Math.round(item.planned_qty)} {item.uom}</TableCell>
                        <TableCell align="right">{item.issued_qty > 0 ? <Typography variant="body2" color={item.is_fulfilled ? 'success.main' : 'warning.main'} fontWeight={600}>{Math.round(item.issued_qty)}</Typography> : '—'}</TableCell>
                        <TableCell><Chip label={item.is_fulfilled ? 'Reserved' : item.issued_qty > 0 ? 'Partial' : dm ? 'Linked' : 'No Alloc'} color={item.is_fulfilled ? 'success' : item.issued_qty > 0 ? 'warning' : dm ? 'info' : 'default'} size="small" /></TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* ═══════════════════════════════════════════════════════════════
          MATERIAL HANDOVER SECTION
         ═══════════════════════════════════════════════════════════════ */}
      {isNotDraftCancelled && hasBomItems && (
        <Paper sx={{ p: 2, mb: 3, border: allMaterialsReceived ? '1px solid' : undefined, borderColor: 'success.main' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
            {allMaterialsReceived ? <VerifiedIcon color="success" /> : materialIssues.length > 0 ? <LocalShippingIcon color="primary" /> : <InventoryIcon color="action" />}
            <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }}>Material Handover</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              {allMaterialsReceived && (
                <Chip label={`Verified by ${order.receiving_verified_by || 'production'}`} color="success" size="small" icon={<VerifiedIcon />} />
              )}
              {order.status !== 'COMPLETED' && order.status !== 'CANCELLED' && (
                <Button size="small" variant="outlined" startIcon={<LocalShippingIcon />} onClick={openIssueDialog}>Issue More</Button>
              )}
              {pendingIssues.length > 0 && (
                <Button size="small" variant="contained" color="success" startIcon={<AssignmentTurnedInIcon />} onClick={openReceiveDialog}>
                  Receive ({pendingIssues.length})
                </Button>
              )}
            </Box>
          </Box>

          {materialIssues.length === 0 && (
            <Alert severity="info" sx={{ mt: 1 }}>Click "Issue" to record material handover to production.</Alert>
          )}

          {materialIssues.length > 0 && (
            isMobile ? (
              <Box sx={{ mt: 1 }}>
                {materialIssues.map((mi) => (
                  <Card key={mi.id} variant="outlined" sx={{ mb: 1, p: 1.5, bgcolor: mi.status === 'DISCREPANCY' ? 'warning.50' : mi.status === 'ISSUED' ? 'action.hover' : undefined }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" fontWeight={600}>{mi.material_name}</Typography>
                      <Chip label={mi.status} size="small" color={mi.status === 'RECEIVED' ? 'success' : mi.status === 'DISCREPANCY' ? 'warning' : 'default'} />
                    </Box>
                    <Typography variant="caption" color="text.secondary">{mi.material_type} | {mi.garment_part}</Typography>
                    <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                      <Typography variant="body2">Issued: {Math.round(mi.issued_qty)} {mi.uom}</Typography>
                      {mi.received_qty !== null && <Typography variant="body2" color={mi.received_qty !== mi.issued_qty ? 'warning.main' : 'success.main'}>Received: {Math.round(mi.received_qty)}</Typography>}
                    </Box>
                    {mi.notes && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>{mi.notes}</Typography>}
                    <Typography variant="caption" color="text.secondary">{mi.issued_by} • {new Date(mi.issued_at).toLocaleDateString('en-IN')}</Typography>
                  </Card>
                ))}
              </Box>
            ) : (
              <TableContainer sx={{ mt: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Material</TableCell>
                      <TableCell>Part</TableCell>
                      <TableCell align="right">Issued</TableCell>
                      <TableCell align="right">Received</TableCell>
                      <TableCell>Details</TableCell>
                      <TableCell>By</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {materialIssues.map((mi) => (
                      <TableRow key={mi.id} sx={{ bgcolor: mi.status === 'DISCREPANCY' ? 'warning.50' : mi.status === 'ISSUED' ? 'action.hover' : undefined }}>
                        <TableCell>{mi.material_name} ({mi.material_type})</TableCell>
                        <TableCell>{mi.garment_part}</TableCell>
                        <TableCell align="right">{Math.round(mi.issued_qty)} {mi.uom}</TableCell>
                        <TableCell align="right">{mi.received_qty !== null ? <Typography variant="body2" fontWeight={600} color={mi.received_qty !== mi.issued_qty ? 'warning.main' : 'success.main'}>{Math.round(mi.received_qty)}</Typography> : <Typography variant="body2" color="text.disabled">Pending</Typography>}</TableCell>
                        <TableCell><Typography variant="caption" color="text.secondary">{mi.notes || '—'}</Typography></TableCell>
                        <TableCell><Typography variant="caption">{mi.issued_by}</Typography></TableCell>
                        <TableCell><Chip label={mi.status} size="small" color={mi.status === 'RECEIVED' ? 'success' : mi.status === 'DISCREPANCY' ? 'warning' : 'default'} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )
          )}

          {materialIssues.length > 0 && (
            <Box sx={{ mt: 1, display: 'flex', gap: 2 }}>
              {completedIssues.length > 0 && <Typography variant="caption" color="success.main">{completedIssues.length} received</Typography>}
              {pendingIssues.length > 0 && <Typography variant="caption" color="text.secondary">{pendingIssues.length} pending</Typography>}
            </Box>
          )}
        </Paper>
      )}

      {/* ═══════════════════════════════════════════════════════════════
          CUTTING SECTION
         ═══════════════════════════════════════════════════════════════ */}
      {showCutting && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <ContentCutIcon color="primary" />
            <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }}>
              Cutting ({cuttingRecords.length})
            </Typography>
          </Box>

          {cuttingRecords.length === 0 ? (
            <Alert severity="info">No cutting records yet. Add cutting to proceed.</Alert>
          ) : isMobile ? (
            cuttingRecords.map((cr: any) => (
              <Card key={cr.id} variant="outlined" sx={{ mb: 1, p: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" fontWeight={600}>Batch #{cr.batch_number} — {cr.garment_part}</Typography>
                  <Chip label={cr.status} size="small" color={cr.status === 'COMPLETED' ? 'success' : 'default'} />
                </Box>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Typography variant="body2">{cr.total_pieces} pcs</Typography>
                  <Typography variant="body2">{cr.fabric_meter} m</Typography>
                  {cr.cutting_date && <Typography variant="body2">{new Date(cr.cutting_date).toLocaleDateString('en-IN')}</Typography>}
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                  {SIZES.map((s) => { const v = cr[SIZE_FIELDS[s]]; return v > 0 ? <Chip key={s} label={`${s}:${v}`} size="small" variant="outlined" /> : null; })}
                </Box>
              </Card>
            ))
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Batch</TableCell>
                    <TableCell>Part</TableCell>
                    {SIZES.map((s) => <TableCell key={s} align="center">{s}</TableCell>)}
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right">Fabric</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cuttingRecords.map((cr: any) => (
                    <TableRow key={cr.id}>
                      <TableCell>#{cr.batch_number}</TableCell>
                      <TableCell>{cr.garment_part}</TableCell>
                      {SIZES.map((s) => <TableCell key={s} align="center">{cr[SIZE_FIELDS[s]] || '-'}</TableCell>)}
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{cr.total_pieces}</TableCell>
                      <TableCell align="right">{cr.fabric_meter} m</TableCell>
                      <TableCell>{cr.cutting_date ? new Date(cr.cutting_date).toLocaleDateString('en-IN') : '-'}</TableCell>
                      <TableCell><Chip label={cr.status} size="small" color={cr.status === 'COMPLETED' ? 'success' : 'default'} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* ═══════════════════════════════════════════════════════════════
          STITCHING SECTION
         ═══════════════════════════════════════════════════════════════ */}
      {showStitching && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <GroupIcon color="warning" />
            <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }}>
              Stitching ({workerAssignments.length})
            </Typography>
          </Box>

          {workerAssignments.length === 0 ? (
            <Alert severity="info">No workers assigned yet. Assign workers after cutting.</Alert>
          ) : isMobile ? (
            workerAssignments.map((wa: any) => (
              <Card key={wa.id} variant="outlined" sx={{ mb: 1, p: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" fontWeight={600}>{wa.worker?.name || wa.worker_id}</Typography>
                  <Chip label={wa.status} size="small" color={wa.status === 'COMPLETED' ? 'success' : 'default'} />
                </Box>
                <Typography variant="caption" color="text.secondary">{wa.garment_part} | {wa.size}</Typography>
                <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                  <Typography variant="body2">Issued: {wa.quantity_issued}</Typography>
                  <Typography variant="body2">Received: {wa.qtyReceived || 0}</Typography>
                </Box>
              </Card>
            ))
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Worker</TableCell>
                    <TableCell>Part</TableCell>
                    <TableCell>Size</TableCell>
                    <TableCell align="right">Issued</TableCell>
                    <TableCell align="right">Received</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {workerAssignments.map((wa: any) => (
                    <TableRow key={wa.id}>
                      <TableCell>{wa.worker?.name || wa.worker_id}</TableCell>
                      <TableCell>{wa.garment_part}</TableCell>
                      <TableCell>{wa.size}</TableCell>
                      <TableCell align="right">{wa.quantity_issued}</TableCell>
                      <TableCell align="right">{wa.qtyReceived || '-'}</TableCell>
                      <TableCell><Chip label={wa.status} size="small" color={wa.status === 'COMPLETED' ? 'success' : 'default'} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* ═══════════════════════════════════════════════════════════════
          FINISHING SECTION
         ═══════════════════════════════════════════════════════════════ */}
      {showFinishing && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <FactoryIcon color="secondary" />
            <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }}>
              Finishing ({productionBatches.length})
            </Typography>
          </Box>

          {productionBatches.length === 0 ? (
            <Alert severity="info">No finishing batches yet. Create batch after stitching.</Alert>
          ) : isMobile ? (
            productionBatches.map((pb: any) => (
              <Card key={pb.id} variant="outlined" sx={{ mb: 1, p: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" fontWeight={600}>{pb.batch_number}</Typography>
                  <Chip label={pb.current_stage} size="small" color="primary" variant="outlined" />
                </Box>
                <Typography variant="caption" color="text.secondary">{pb.garment_part || '-'}</Typography>
                <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                  <Typography variant="body2">Total: {pb.total_issued}</Typography>
                  <Typography variant="body2">Barcoded: {pb.total_barcoded}</Typography>
                  <Typography variant="body2">Repair: {pb.total_in_repair}</Typography>
                </Box>
              </Card>
            ))
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Batch</TableCell>
                    <TableCell>Part</TableCell>
                    <TableCell>Stage</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right">Barcoded</TableCell>
                    <TableCell align="right">Repair</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {productionBatches.map((pb: any) => (
                    <TableRow key={pb.id}>
                      <TableCell>{pb.batch_number}</TableCell>
                      <TableCell>{pb.garment_part || '-'}</TableCell>
                      <TableCell><Chip label={pb.current_stage} size="small" color="primary" variant="outlined" /></TableCell>
                      <TableCell align="right">{pb.total_issued}</TableCell>
                      <TableCell align="right">{pb.total_barcoded}</TableCell>
                      <TableCell align="right">{pb.total_in_repair}</TableCell>
                      <TableCell><Chip label={pb.status} size="small" /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* ═══ Design BOM Template ═══ */}
      {bomItems.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>Design BOM Template</Typography>
          {isMobile ? (
            bomItems.map((item) => (
              <Card key={item.id} variant="outlined" sx={{ mb: 1, p: 1.5 }}>
                <Typography variant="body2" fontWeight={600}>{item.material_name}</Typography>
                <Typography variant="caption" color="text.secondary">{item.material_type} | {item.garment_part} | {item.consumption_per_piece} {item.uom}/pc | {item.wastage_pct}% wastage</Typography>
              </Card>
            ))
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Material</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Part</TableCell>
                    <TableCell align="right">Per Piece</TableCell>
                    <TableCell>UOM</TableCell>
                    <TableCell align="right">Wastage %</TableCell>
                    <TableCell align="right">Rate</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bomItems.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>{item.material_name}</TableCell>
                      <TableCell>{item.material_type}</TableCell>
                      <TableCell>{item.garment_part}</TableCell>
                      <TableCell align="right">{item.consumption_per_piece}</TableCell>
                      <TableCell>{item.uom}</TableCell>
                      <TableCell align="right">{item.wastage_pct}%</TableCell>
                      <TableCell align="right">{item.estimated_rate || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* ═══════════════════════════════════════════════════════════════
          DIALOGS
         ═══════════════════════════════════════════════════════════════ */}

      {/* ── Issue Materials Dialog ── */}
      <Dialog open={issueDialogOpen} onClose={() => !issuingMaterials && setIssueDialogOpen(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocalShippingIcon color="primary" />
            <Typography variant="h6">Issue Materials to Production</Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter quantities to issue. For replacements (damaged/lost), set additional qty as needed.
          </Typography>
          {issueRows.map((row, idx) => (
            <Card key={row.bom_item_id} variant="outlined" sx={{ mb: 1.5, p: 2 }}>
              <Box sx={{ display: 'flex', gap: 1.5, mb: 1 }}>
                <Avatar
                  src={row.material_image || undefined}
                  variant="rounded"
                  sx={{ width: 44, height: 44, bgcolor: 'grey.200', fontSize: 11 }}
                >
                  {!row.material_image && (row.material_code?.slice(0, 3) || row.material_name.slice(0, 2))}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body1" fontWeight={600}>{row.material_name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {row.material_code && <>{row.material_code} • </>}{row.material_type} | {row.garment_part}
                  </Typography>
                </Box>
                <Chip label={row.uom} size="small" variant="outlined" />
              </Box>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={4} sm={2}>
                  <Typography variant="caption" color="text.secondary">Required</Typography>
                  <Typography variant="body2" fontWeight={600}>{Math.round(row.planned_qty)}</Typography>
                </Grid>
                <Grid item xs={4} sm={2}>
                  <Typography variant="caption" color="text.secondary">Already Issued</Typography>
                  <Typography variant="body2" color={row.already_issued > 0 ? 'info.main' : 'text.secondary'}>{row.already_issued}</Typography>
                </Grid>
                <Grid item xs={4} sm={2}>
                  <Typography variant="caption" color="text.secondary">Reserved</Typography>
                  <Typography variant="body2">{Math.round(row.reserved_qty)}</Typography>
                </Grid>
                <Grid item xs={6} sm={2}>
                  <TextField
                    label="Issuing Qty"
                    type="number"
                    size="small"
                    fullWidth
                    value={row.issued_qty}
                    onChange={(e) => {
                      const updated = [...issueRows];
                      updated[idx] = { ...updated[idx], issued_qty: Math.round(Number(e.target.value) || 0) };
                      setIssueRows(updated);
                    }}
                    inputProps={{ min: 0, step: 1 }}
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField
                    label="Packets / Lumps / Details"
                    size="small"
                    fullWidth
                    value={row.notes}
                    onChange={(e) => {
                      const updated = [...issueRows];
                      updated[idx] = { ...updated[idx], notes: e.target.value };
                      setIssueRows(updated);
                    }}
                    placeholder="e.g. 2 packets, 1 lump"
                  />
                </Grid>
              </Grid>
            </Card>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIssueDialogOpen(false)} disabled={issuingMaterials}>Cancel</Button>
          <Button variant="contained" onClick={handleIssueMaterials} disabled={issuingMaterials || issueRows.every((r) => r.issued_qty === 0)} startIcon={issuingMaterials ? <CircularProgress size={14} color="inherit" /> : <LocalShippingIcon />}>
            {issuingMaterials ? 'Issuing...' : 'Confirm Issue'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Receive Materials Dialog ── */}
      <Dialog open={receiveDialogOpen} onClose={() => !receivingMaterials && setReceiveDialogOpen(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssignmentTurnedInIcon color="success" />
            <Typography variant="h6">Confirm Received Materials</Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {receiveRows.map((row, idx) => {
            const hasDiscrepancy = row.received_qty !== row.issued_qty;
            return (
              <Card key={row.issue_id} variant="outlined" sx={{ mb: 1.5, p: 2, bgcolor: hasDiscrepancy ? 'warning.50' : undefined }}>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body1" fontWeight={600}>{row.material_name}</Typography>
                  <Typography variant="caption" color="text.secondary">{row.material_type} | {row.garment_part}</Typography>
                </Box>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">Issued</Typography>
                    <Typography variant="body2" fontWeight={600}>{Math.round(row.issued_qty)} {row.uom}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label="Received"
                      type="number"
                      size="small"
                      fullWidth
                      value={row.received_qty}
                      onChange={(e) => {
                        const updated = [...receiveRows];
                        updated[idx] = { ...updated[idx], received_qty: Number(e.target.value) || 0 };
                        setReceiveRows(updated);
                      }}
                      inputProps={{ min: 0, step: 0.01 }}
                      error={hasDiscrepancy}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label="Notes"
                      size="small"
                      fullWidth
                      value={row.discrepancy_notes}
                      onChange={(e) => {
                        const updated = [...receiveRows];
                        updated[idx] = { ...updated[idx], discrepancy_notes: e.target.value };
                        setReceiveRows(updated);
                      }}
                      placeholder={hasDiscrepancy ? 'Reason' : 'Optional'}
                      error={hasDiscrepancy && !row.discrepancy_notes}
                    />
                  </Grid>
                </Grid>
              </Card>
            );
          })}
          {receiveRows.some((r) => r.received_qty !== r.issued_qty) && (
            <Alert severity="warning" sx={{ mt: 1 }}>Some quantities differ — will be marked as discrepancy.</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReceiveDialogOpen(false)} disabled={receivingMaterials}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleReceiveMaterials} disabled={receivingMaterials} startIcon={receivingMaterials ? <CircularProgress size={14} color="inherit" /> : <VerifiedIcon />}>
            {receivingMaterials ? 'Confirming...' : 'Confirm Receipt'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Status Change Dialog ── */}
      <Dialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)}>
        <DialogTitle>Change Order Status</DialogTitle>
        <DialogContent>
          <TextField select fullWidth label="New Status" value={newStatus} onChange={(e) => setNewStatus(e.target.value as ProductionOrderStatus)} sx={{ mt: 1 }}>
            {Object.entries(STATUS_LABELS).map(([val, label]) => (
              <MenuItem key={val} value={val}>{label}</MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleStatusChange}>Update</Button>
        </DialogActions>
      </Dialog>

      {/* ═══ Snackbar ═══ */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
